import type { CefrLevel, StudyMode } from "@rmnlng/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { apiFetch, getToken, setToken } from "./api";

export type UserRole = "USER" | "ADMIN";
export type AuthUser = {
  id: string;
  email: string;
  // Display name. Captured at signup, editable in Settings. Null if
  // user signed up before the field was added or left it blank — the
  // greeting on Home falls back to the email prefix in that case.
  name: string | null;
  role: UserRole;
  // Daily new-card caps. `dailyBatchSize` covers vocab specifically;
  // grammar/quiz have their own knobs because their default volume is much
  // smaller (one rule == a concept, ~5x quizzes per rule). 0 disables that
  // kind's introduction without affecting the others.
  dailyBatchSize: number;
  newGrammarPerDay: number;
  newQuizPerDay: number;
  locale: "ru" | "ro";
  // OPEN = study anything (current behavior), LEVEL = guided path scoped
  // to `currentLevel`. Free to flip via the Home header switch.
  studyMode: StudyMode;
  // The level the user is currently working through. Read-only from the
  // client's perspective — server advances it via the level-up exam.
  currentLevel: CefrLevel;
  // ISO timestamp when the user clicked the verification link. NULL =
  // not yet verified. Soft verification: the app does NOT gate any
  // behaviour on this in v1, but a future "verify your email" banner
  // will dismiss itself once it's non-null. The verification flow
  // updates this via /auth/email/verify/confirm; the web invalidates
  // the me cache after success so banners disappear immediately.
  emailVerifiedAt: string | null;
};

export function isAdmin(user: AuthUser | null | undefined): boolean {
  return user?.role === "ADMIN";
}

type LoginResponse = { token: string; user: AuthUser };

const ME_KEY = ["auth", "me"] as const;

export function useMe() {
  return useQuery({
    queryKey: ME_KEY,
    queryFn: () => apiFetch<AuthUser>("/auth/me"),
    enabled: !!getToken(),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; password: string }) =>
      apiFetch<LoginResponse>("/auth/login", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: (res) => {
      setToken(res.token);
      qc.setQueryData(ME_KEY, res.user);
    },
  });
}

// Self-service signup. Server returns the same `LoginResponse` shape on
// success so the post-success bookkeeping is identical to login —
// new user is signed in immediately, no second-step redirect to /login.
//
// `confirmPassword` is sent through but server-side ignored (the Zod
// schema validates the match, then drops the field). Including it in
// the mutation input keeps the form's wire shape and the schema's
// shape in sync.
export function useRegister() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; password: string; confirmPassword: string }) =>
      apiFetch<LoginResponse>("/auth/register", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: (res) => {
      setToken(res.token);
      qc.setQueryData(ME_KEY, res.user);
    },
  });
}

// Step 1 of the "I forgot my password" flow. The server **always**
// returns 200 (anti-enumeration — see `email-scaffold.md`), so this
// mutation only resolves with `{ ok: true }` on a 2xx, never with a
// "user not found" error. The UI treats success identically: render
// the "check your email" confirmation screen regardless.
export function useForgotPassword() {
  return useMutation({
    mutationFn: (input: { email: string }) =>
      apiFetch<{ ok: true }>("/auth/password/forgot", {
        method: "POST",
        body: JSON.stringify(input),
      }),
  });
}

// Step 2 of the "I forgot my password" flow. 400 on bad/expired/used
// token (server doesn't differentiate to avoid leaking which); the UI
// maps that to "this link is no longer valid, request a new one".
export function useResetPassword() {
  return useMutation({
    mutationFn: (input: { token: string; password: string; confirmPassword: string }) =>
      apiFetch<{ ok: true }>("/auth/password/reset", {
        method: "POST",
        body: JSON.stringify(input),
      }),
  });
}

// Re-send the email verification link. **Auth required** — the
// server reads the user from the JWT and refuses unauthenticated
// callers. Idempotent: existing unused tokens are invalidated and a
// new one is issued. Used by the /verify-email page's "resend" button
// and (eventually) by a future "verify your email" banner.
export function useResendVerification() {
  return useMutation({
    mutationFn: () =>
      apiFetch<{ ok: true }>("/auth/email/verify/request", {
        method: "POST",
      }),
  });
}

// Confirm the verification link. Public — the user might be on a
// device that isn't signed in (followed the link from email on a
// different browser), so we don't require a JWT. On success, the
// caller invalidates the me cache so any future banner-dismissal
// logic sees the verified state.
export function useConfirmVerification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { token: string }) =>
      apiFetch<{ ok: true }>("/auth/email/verify/confirm", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      // Refetch /auth/me so `useMe()` returns the new
      // `emailVerifiedAt`. Without this, signed-in users on the same
      // browser would still see `null` until they refresh.
      qc.invalidateQueries({ queryKey: ME_KEY });
    },
  });
}

export type UpdateMeInput = {
  // Display name. Empty string clears it (server stores null);
  // non-empty trims to the stored value.
  name?: string;
  dailyBatchSize?: number;
  newGrammarPerDay?: number;
  newQuizPerDay?: number;
  locale?: "ru" | "ro";
  // OPEN | LEVEL. Triggered by `StudyModeSection` in Settings (the
  // Home-header switcher was moved there in v1.x).
  studyMode?: StudyMode;
  // Manual level selection — Settings escape hatch in
  // `<CurrentLevelSection>`. The exam path in `levelup.service` is
  // still the canonical advance mechanism for users who don't go
  // looking for this. Going backward is also supported (revisit
  // earlier-level cards while keeping current SRS state intact).
  currentLevel?: CefrLevel;
};

export function useUpdateMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateMeInput) =>
      apiFetch<AuthUser>("/auth/me", {
        method: "PATCH",
        body: JSON.stringify(input),
      }),
    onSuccess: (user) => {
      // Seed the fresh profile into the me cache so the UI updates instantly
      // without waiting for a refetch round-trip.
      qc.setQueryData(ME_KEY, user);
      // Batch size + study mode both affect what the queue serves —
      // invalidate so Home re-fetches with the new shape.
      qc.invalidateQueries({ queryKey: ["reviews", "queue"] });
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return () => {
    setToken(null);
    qc.clear();
    navigate("/login", { replace: true });
  };
}
